import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
} from "@reduxjs/toolkit";
import { uncoveryApi } from "../../services";

const entitiesAdapter = createEntityAdapter();
const initialState = entitiesAdapter.getInitialState({
  cartographyId: "",
  entity: {
    data: [],
    selected: null,
    fetchAllStatus: "idle",
    fetchAllErrorMessage: "",
    fetchOneStatus: "idle",
    fetchOneErrorMessage: "",
    hasNextPage: false,
    page: 1,
    pageSize: 50,
    sortBy: "createdAt",
    orderBy: "desc",
    searchTerm: "",
    count: 0,
  },
  graph: {
    data: [],
    selected: {},
    fetchAllStatus: "idle",
    fetchAllErrorMessage: "",
    fetchOneStatus: "idle",
    fetchOneErrorMessage: "",
    hasNextPage: false,
    page: 1,
    pageSize: 50,
    sortBy: "createdAt",
    orderBy: "desc",
    searchTerm: "",
    count: 0,
  },
  asset: {
    data: [],
    selected: {},
    fetchAllStatus: "idle",
    fetchAllErrorMessage: "",
    fetchOneStatus: "idle",
    fetchOneErrorMessage: "",
    hasNextPage: false,
    page: 1,
    pageSize: 50,
    sortBy: "createdAt",
    orderBy: "desc",
    searchTerm: "",
    count: 0,
  },
  addStatus: "idle",
  addErrorMessage: "",
});

export const fetchAllCartographiesEntity = createAsyncThunk(
  "cartographies/fetchAllCartographiesEntity",
  async ({ entityId }, { rejectWithValue, getState }) => {
    try {
      const { page, pageSize, sortBy, orderBy, searchTerm } =
        getState().cartography.entity;
      const response = await uncoveryApi.get(
        `/entities/${entityId}/cartographies?page=${page}&pageSize=${pageSize}&sortBy=${sortBy}&orderBy=${orderBy}&searchTerm=${searchTerm}`
      );
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  }
);

export const fetchAllCartographiesAsset = createAsyncThunk(
  "cartographies/fetchAllCartographiesAsset",
  async ({ entityId, assetId, view }, { rejectWithValue, getState }) => {
    try {
      const { page, pageSize, sortBy, orderBy, searchTerm } =
        getState().cartography[view];
      const response = await uncoveryApi.get(
        `/entities/${entityId}/assets/${assetId}/cartographies?page=${page}&pageSize=${pageSize}&sortBy=${sortBy}&orderBy=${orderBy}&searchTerm=${searchTerm}`
      );
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  }
);

export const fetchOneCartography = createAsyncThunk(
  "cartographies/fetchOneCartography",
  async ({ entityId, cartographyId, view }, { rejectWithValue, getState }) => {
    try {
      const response = await uncoveryApi.get(
        `/entities/${entityId}/cartographies/${cartographyId}`
      );
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  }
);

export const addCartography = createAsyncThunk(
  "cartographies/addCartography",
  async ({ entityId }, { rejectWithValue, getState }) => {
    try {
      const response = await uncoveryApi.post(
        `/entities/${entityId}/cartographies`
      );
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  }
);

export const cartographySlice = createSlice({
  name: "cartography",
  initialState,
  reducers: {
    updateCartographySelected: (state, { payload }) => {
      const { cartographyId, view } = payload;
      const index = state[view].data.findIndex((c) => c.id === cartographyId);
      state[view].selected = state[view].data[index];
      return state;
    },
    refreshAddCartographyStatus: (state) => {
      state.addStatus = "idle";
      return state;
    },
    updatePage: (state, { payload }) => {
      const { view, page } = payload;
      state[view].page = page;
      return state;
    },
    updateSearchTerm: (state, { payload }) => {
      const { view, searchTerm } = payload;
      state[view].page = 1;
      state[view].searchTerm = searchTerm;
      return state;
    },
    resetCartographyState: () => initialState,
  },
  extraReducers: {
    [fetchAllCartographiesEntity.pending]: (state, { payload }) => {
      state.entity.fetchAllStatus = "loading";
    },
    [fetchAllCartographiesEntity.fulfilled]: (state, { payload }) => {
      state.entity.values = [...state.entity.data, ...payload.data];
      state.entity.hasNextPage = payload.pageInfo.hasNextPage;
      state.entity.count = payload.pageInfo.count;
      state.entity.fetchAllStatus = "success";
    },
    [fetchAllCartographiesEntity.rejected]: (state, { payload }) => {
      state.entity.fetchAllStatus = "error";
      state.entity.fetchAllErrorMessage = payload.message;
    },
    [fetchAllCartographiesAsset.pending]: (state, { payload, meta }) => {
      const { view } = meta.arg;
      state[view].fetchAllStatus = "loading";
    },
    [fetchAllCartographiesAsset.fulfilled]: (state, { payload, meta }) => {
      const { view } = meta.arg;
      if (state[view].page === 1) {
        state[view].data = payload.data;
      } else {
        state[view].data = [...state[view].data, ...payload.data];
      }
      state[view].hasNextPage = payload.pageInfo.hasNextPage;
      state[view].count = payload.pageInfo.count;
      state[view].fetchAllStatus = "success";
    },
    [fetchAllCartographiesAsset.rejected]: (state, { payload, meta }) => {
      const { view } = meta.arg;
      state[view].fetchAllStatus = "error";
      state[view].fetchAllErrorMessage = payload.message;
    },
    [addCartography.pending]: (state, { payload }) => {
      state.addStatus = "loading";
    },
    [addCartography.fulfilled]: (state, { payload }) => {
      state.addStatus = "success";
    },
    [addCartography.rejected]: (state, { payload }) => {
      state.addStatus = "error";
      state.addErrorMessage = payload.message;
    },
    [fetchOneCartography.pending]: (state, { payload, meta }) => {
      const { view } = meta.arg;
      state[view].fetchOneStatus = "loading";
    },
    [fetchOneCartography.fulfilled]: (state, { payload, meta }) => {
      const { view } = meta.arg;
      state[view].selected = payload.data;
      state[view].fetchOneStatus = "success";
    },
    [fetchOneCartography.rejected]: (state, { payload, meta }) => {
      const { view } = meta.arg;
      state[view].fetchOneStatus = "error";
      state[view].fetchOneErrorMessage = payload.message;
    },
  },
});

export const {
  updateCartographySelected,
  refreshAddCartographyStatus,
  updatePage,
  resetCartographyState,
  updateSearchTerm,
} = cartographySlice.actions;

export default cartographySlice.reducer;

export const selectCartographyById = (state, cartographyId, view) => {
  return state.cartography[view].data.find((v) => v.id === cartographyId);
};
export const selectCartographyIds = (state, view) => {
  return state.cartography[view].data.map((v) => v.id);
};
export const selectCartographySelected = (state, view) => {
  return state.cartography[view].data[0];
};
